import { useCallback, useMemo } from 'react';
import { computeMasterCheckboxState, toggleScopedSelection } from './masterCheckboxState';

export interface TriStateMasterCheckbox {
  /** All scoped values are currently selected. */
  readonly checked: boolean;
  /** Some (but not all) scoped values are selected. */
  readonly indeterminate: boolean;
  /** Whether toggling is meaningful — false when there are no scoped values. */
  readonly hasItems: boolean;
  /**
   * Toggle: clears every scoped value when `checked`, otherwise selects every
   * scoped value (merged with the current selection so values outside the
   * scope stay intact).
   */
  readonly toggle: () => void;
}

export interface UseTriStateMasterCheckboxOptions<T> {
  /** Values that the master checkbox controls (typically the current filter view). */
  readonly scopedValues: readonly T[];
  /** Currently selected values across the whole selection (may include values outside scope). */
  readonly selected: readonly T[];
  /** Called with the new selection. */
  readonly onChange: (next: readonly T[]) => void;
}

/**
 * Tri-state master checkbox helper.
 *
 * Drives a master "select all in this list" checkbox sitting above a
 * filtered set of selectable items. Returns the three visual states
 * needed by `<Checkbox checked indeterminate />` plus a single `toggle`
 * action that does the right thing in all three cases.
 *
 * Toggle semantics:
 * - When all scoped values are selected → remove just those scoped values
 *   (values outside the scope stay selected).
 * - When none/some scoped values are selected → add every scoped value
 *   to the selection.
 *
 * @example
 *   const master = useTriStateMasterCheckbox({
 *     scopedValues: filtered.map((x) => x.id),
 *     selected: draft,
 *     onChange: setDraft,
 *   });
 *   <Checkbox
 *     checked={master.checked}
 *     indeterminate={master.indeterminate}
 *     onChange={master.toggle}
 *   />
 */
export function useTriStateMasterCheckbox<T>({
  scopedValues,
  selected,
  onChange,
}: UseTriStateMasterCheckboxOptions<T>): TriStateMasterCheckbox {
  const { checked, indeterminate, hasItems } = useMemo(
    () => computeMasterCheckboxState(scopedValues, selected),
    [scopedValues, selected],
  );

  const toggle = useCallback(() => {
    onChange(toggleScopedSelection(scopedValues, selected));
  }, [scopedValues, selected, onChange]);

  return { checked, indeterminate, hasItems, toggle };
}
